/// <reference lib="dom" />
// Markdown ↔ HTML converters + text-extraction utilities for the blog editor.
// Markdown→HTML is regex-based (no deps). HTML→markdown is a recursive DOM walk.
// htmlToPlainText lives here too — it's the safe, block-aware version.

function inlineFormat(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" loading="lazy">')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/_(.+?)_/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>");
}

// GFM pipe-table separator row, e.g. "| --- | :---: | ---: |" or "---|---".
function isTableSeparatorRow(line: string): boolean {
  return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?$/.test(line.trim());
}

function splitTableRow(line: string): string[] {
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.trim());
}

export function markdownToHtml(md: string): string {
  const lines = md.split("\n");
  const out: string[] = [];
  let inUl = false;
  let inOl = false;
  let i = 0;

  const closeList = () => {
    if (inUl) { out.push("</ul>"); inUl = false; }
    if (inOl) { out.push("</ol>"); inOl = false; }
  };

  while (i < lines.length) {
    const line = lines[i].trimEnd();

    if (!line.trim()) {
      // Real production bug (2026-08-06): a blank line unconditionally closed
      // any open list, so a "loose" list -- list items separated by blank
      // lines, which is how most AI-generated numbered/bulleted content is
      // actually written (one full paragraph per step) -- got split into N
      // separate single-item <ol>/<ul> elements instead of one continuous
      // list. Rendered as every item showing "1." (live-confirmed: a "5
      // Actionable Steps" post showed all five items numbered 1). CommonMark
      // treats this as a single loose list; a blank line should only end the
      // list when what follows genuinely isn't a continuation of it. Peek
      // past this blank line (and any further blank lines) to the next real
      // line -- if it's a same-type list-item line, keep the list open.
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      const next = j < lines.length ? lines[j] : "";
      const nextContinuesUl = inUl && /^[*-] /.test(next);
      const nextContinuesOl = inOl && /^\d+\. /.test(next);
      if (!nextContinuesUl && !nextContinuesOl) closeList();
      i++;
      continue;
    }

    // A GFM table is a header row immediately followed by a separator row —
    // must check the lookahead before falling into the generic paragraph
    // branch below, which otherwise wraps each row as its own <p> and loses
    // the table entirely.
    if (line.includes("|") && i + 1 < lines.length && isTableSeparatorRow(lines[i + 1])) {
      closeList();
      const headerCells = splitTableRow(line);
      out.push(
        "<table><thead><tr>" +
          headerCells.map((c) => `<th>${inlineFormat(c)}</th>`).join("") +
          "</tr></thead><tbody>"
      );
      i += 2; // skip header row + separator row
      while (i < lines.length && lines[i].trim().includes("|")) {
        const cells = splitTableRow(lines[i]);
        out.push("<tr>" + cells.map((c) => `<td>${inlineFormat(c)}</td>`).join("") + "</tr>");
        i++;
      }
      out.push("</tbody></table>");
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      // GFM horizontal rule — nodeToMd already emits "---" for an <hr> on
      // the HTML->markdown side, but this direction had no matching case,
      // so a rule round-tripped through Rich Text (or typed directly here)
      // came back as a literal "---" paragraph instead of a real <hr>.
      closeList();
      out.push("<hr>");
    } else if (/^#### /.test(line)) {
      closeList();
      out.push(`<h4>${inlineFormat(line.slice(5))}</h4>`);
    } else if (/^### /.test(line)) {
      closeList();
      out.push(`<h3>${inlineFormat(line.slice(4))}</h3>`);
    } else if (/^## /.test(line)) {
      closeList();
      out.push(`<h2>${inlineFormat(line.slice(3))}</h2>`);
    } else if (/^# /.test(line)) {
      closeList();
      out.push(`<h1>${inlineFormat(line.slice(2))}</h1>`);
    } else if (/^[*-] /.test(line)) {
      if (inOl) { out.push("</ol>"); inOl = false; }
      if (!inUl) { out.push("<ul>"); inUl = true; }
      out.push(`<li>${inlineFormat(line.slice(2))}</li>`);
    } else if (/^\d+\. /.test(line)) {
      if (inUl) { out.push("</ul>"); inUl = false; }
      if (!inOl) { out.push("<ol>"); inOl = true; }
      out.push(`<li>${inlineFormat(line.replace(/^\d+\. /, ""))}</li>`);
    } else if (/^> /.test(line)) {
      closeList();
      out.push(`<blockquote><p>${inlineFormat(line.slice(2))}</p></blockquote>`);
    } else {
      closeList();
      out.push(`<p>${inlineFormat(line)}</p>`);
    }
    i++;
  }

  closeList();
  return out.join("\n");
}

function nodeToMd(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";

  const el = node as Element;
  const inner = () => Array.from(el.childNodes).map(nodeToMd).join("");

  switch (el.tagName) {
    case "H1": return `# ${inner()}\n\n`;
    case "H2": return `## ${inner()}\n\n`;
    case "H3": return `### ${inner()}\n\n`;
    case "H4": return `#### ${inner()}\n\n`;
    case "H5": return `##### ${inner()}\n\n`;
    case "H6": return `###### ${inner()}\n\n`;
    case "STRONG": return `**${inner()}**`;
    case "EM": return `*${inner()}*`;
    case "CODE": return `\`${inner()}\``;
    case "A": return `[${inner()}](${el.getAttribute("href") ?? ""})`;
    case "IMG": {
      const src = el.getAttribute("src") ?? "";
      const alt = el.getAttribute("alt") ?? "image";
      // Preserve blob/pending images as placeholder — they'll re-upload on save
      return src.startsWith("blob:") ? `![${alt}](${src})` : `![${alt}](${src})`;
    }
    case "P": {
      const text = inner().trim();
      return text ? `${text}\n\n` : "";
    }
    case "BR": return "\n";
    case "UL": return Array.from(el.children).map((li) => `- ${nodeToMd(li).trim()}\n`).join("") + "\n";
    case "OL": return Array.from(el.children).map((li, i) => `${i + 1}. ${nodeToMd(li).trim()}\n`).join("") + "\n";
    case "LI": return inner();
    case "BLOCKQUOTE": return `> ${inner().trim()}\n\n`;
    case "HR": return "---\n\n";
    case "TABLE": {
      // Rebuild as a single GFM pipe-table block with no blank lines between
      // rows — a blank line between what were originally consecutive rows
      // (e.g. from naively emitting each <tr> the way the P case emits
      // "\n\n" after every block) breaks remark-gfm's table parser, which
      // requires the header, separator, and data rows to be contiguous.
      const cellText = (cell: Element) => nodeToMd(cell).replace(/\n+/g, " ").trim() || " ";
      const headCells = Array.from(el.querySelectorAll(":scope > thead > tr > th")).map(cellText);
      if (headCells.length === 0) return `${inner()}\n\n`; // malformed table, fall back to plain text
      const bodyRows = Array.from(el.querySelectorAll(":scope > tbody > tr")).map((tr) =>
        Array.from(tr.children).map(cellText)
      );
      const header = `| ${headCells.join(" | ")} |`;
      const sep = `| ${headCells.map(() => "---").join(" | ")} |`;
      const rows = bodyRows.map((cells) => `| ${cells.join(" | ")} |`);
      return [header, sep, ...rows].join("\n") + "\n\n";
    }
    default: return inner();
  }
}

export function htmlToMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return Array.from(doc.body.childNodes).map(nodeToMd).join("").replace(/\n{3,}/g, "\n\n").trim();
}

const HTML_ENTITY_MAP: Record<string, string> = {
  amp: "&", nbsp: " ", lt: "<", gt: ">", quot: '"', "#39": "'", apos: "'",
};
function decodeEntities(s: string): string {
  return s.replace(/&(amp|nbsp|lt|gt|quot|#39|apos);/g, (_, e) => HTML_ENTITY_MAP[e] ?? _);
}

/**
 * Same job as htmlToMarkdown, but pure regex/string manipulation — no
 * DOMParser. DOMParser is a browser-only global; this app renders blog posts
 * through a TanStack Start SSR loader, and calling htmlToMarkdown during that
 * server render would throw (no DOM in the Node runtime) or, if guarded and
 * skipped there, silently diverge from the client's post-hydration output —
 * a hydration mismatch, and worse, the wrong FAQ/HowTo/checklist extraction
 * shown to crawlers reading the server-rendered HTML. This version behaves
 * identically in both environments since it never touches a DOM API.
 * Table/list/entity handling only needs to be as complete as extractFaqs /
 * extractHowToSteps / extractChecklist / extractHeadings actually require
 * (heading markers, list-item markers, GFM pipe-table rows) — not a general
 * fidelity HTML→Markdown converter.
 */
// Converts inline formatting (bold/italic/links) to markdown, then strips
// whatever tags are left and decodes entities. Used for text inside block
// containers (list items, table cells) so those don't lose bold/link markup
// the way a blind tag-strip would — extractHowToSteps in particular relies
// on a **bold lead-in** surviving inside a list item to split a step's name
// from its body text.
function inlineToMd(html: string): string {
  const withInline = html
    .replace(/<(strong|b)[^>]*>([\s\S]*?)<\/\1>/gi, (_, __, t) => `**${t}**`)
    .replace(/<(em|i)[^>]*>([\s\S]*?)<\/\1>/gi, (_, __, t) => `*${t}*`)
    .replace(/<a[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_, href, t) => `[${t}](${href})`);
  return decodeEntities(withInline.replace(/<[^>]+>/g, "").trim());
}

export function htmlToMarkdownSsrSafe(html: string): string {
  let out = html;
  out = out.replace(/<(script|style|head|title)[^>]*>[\s\S]*?<\/\1>/gi, "");
  out = out.replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, "");

  out = out.replace(/<table[^>]*>([\s\S]*?)<\/table>/gi, (_, tableInner: string) => {
    const rows = Array.from(tableInner.matchAll(/<tr[^>]*>([\s\S]*?)<\/tr>/gi)).map((rowMatch) => {
      const cells = Array.from((rowMatch[1] as string).matchAll(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi))
        .map((cellMatch) => inlineToMd(cellMatch[1] as string));
      return `| ${cells.join(" | ")} |`;
    });
    if (rows.length === 0) return "\n\n";
    const colCount = (rows[0].match(/\|/g)?.length ?? 2) - 1;
    const sep = `|${" --- |".repeat(Math.max(colCount, 1))}`;
    return `\n\n${rows[0]}\n${sep}\n${rows.slice(1).join("\n")}\n\n`;
  });

  out = out.replace(/<(ul|ol)[^>]*>([\s\S]*?)<\/\1>/gi, (_, tag: string, listInner: string) => {
    let n = 0;
    const items = Array.from(listInner.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)).map((itemMatch) => {
      n += 1;
      const text = inlineToMd(itemMatch[1] as string);
      return tag.toLowerCase() === "ol" ? `${n}. ${text}` : `- ${text}`;
    });
    return `\n${items.join("\n")}\n`;
  });

  out = out
    .replace(/<h2[^>]*>([\s\S]*?)<\/h2>/gi, (_, t) => `\n\n## ${inlineToMd(t)}\n\n`)
    .replace(/<h3[^>]*>([\s\S]*?)<\/h3>/gi, (_, t) => `\n\n### ${inlineToMd(t)}\n\n`)
    .replace(/<h[4-6][^>]*>([\s\S]*?)<\/h[4-6]>/gi, (_, t) => `\n\n#### ${inlineToMd(t)}\n\n`)
    .replace(/<br\s*\/?>/gi, "  \n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<(strong|b)[^>]*>([\s\S]*?)<\/\1>/gi, (_, __, t) => `**${t}**`)
    .replace(/<(em|i)[^>]*>([\s\S]*?)<\/\1>/gi, (_, __, t) => `*${t}*`)
    .replace(/<a[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_, href, t) => `[${t}](${href})`);

  out = out.replace(/<[^>]+>/g, "");
  out = decodeEntities(out);
  out = out.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  return out;
}

// DOMParser + body.textContent smashes adjacent block elements together with
// zero separator — "<p>A.</p><p>B.</p>" becomes "A.B." which breaks sentence
// detection. This inserts spaces at block boundaries first, then strips tags.
export function htmlToPlainText(html: string): string {
  return html
    .replace(/<\/?(p|h[1-6]|li|div|br|td|th|blockquote|section|article)[^>]*>/gi, " ")
    .replace(/<[^>]+>/g, "")
    // &amp; must decode before &nbsp; — otherwise &amp;nbsp; stays as &nbsp; after pass 1
    .replace(/&amp;/g, "&")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

// Belt-and-suspenders guard for the markdown `content` field, mirroring
// stripUnresolvedBlobImages for content_html: a blob: URL is only ever valid
// in the browser tab/session that created it, so it must never be what gets
// persisted — not even inside markdown image syntax ![alt](blob:...), which
// can happen if a user switches Markdown↔Rich Text tabs while an image
// upload is still in flight.
export function stripBlobImageMarkdown(md: string): string {
  if (!md || !md.includes("(blob:")) return md;
  return md.replace(/!\[[^\]]*\]\(blob:[^)]*\)\n*/g, "");
}

// Sentence-aware truncation — ends at a clean "." "!" or "?" when possible.
// The 50% floor prevents using a 3-word sentence as the cutoff for a 160-char block.
// Falls back to word boundary (+ "…") then hard-slice if there's no whitespace at all.
export function smartTruncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  const window = text.slice(0, maxLen + 1);
  const sentenceEnd = Math.max(
    window.lastIndexOf(". "),
    window.lastIndexOf("! "),
    window.lastIndexOf("? "),
  );
  // Only use the sentence boundary if it's past the halfway point
  if (sentenceEnd > Math.floor(maxLen * 0.5)) {
    return text.slice(0, sentenceEnd + 1).trim();
  }
  const lastSpace = text.lastIndexOf(" ", maxLen);
  if (lastSpace > 0) return text.slice(0, lastSpace).trimEnd() + "…";
  return text.slice(0, maxLen).trimEnd() + "…";
}
